<script>
import { useVuelidate } from '@vuelidate/core';
import ButtonNext from 'next/button/Button.vue';
import Draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import getUuid from 'widget/helpers/uuid';
import { setStepMoveInProgress } from 'dashboard/store/utils/wgptAutomationBuilderUtils';
import AddStepButton from '../../addStepDropdown/AddStepButton.vue';
import Dropdown from '../../Dropdown.vue';
import conditionValidationMixins from './helpers/conditionValidationMixins';

export default {
  components: {
    AddStepButton,
    Dropdown,
    ButtonNext,
    Draggable,
  },
  mixins: [conditionValidationMixins],
  provide() {
    return {
      stepAncestorIds: [...this.parentStepAncestorIds, this.step.id],
    };
  },
  inject: {
    parentStepAncestorIds: {
      from: 'stepAncestorIds',
      default: () => [],
    },
  },
  props: {
    step: {
      type: Object,
      default: () => {},
    },
    first: {
      type: Boolean,
      default: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  // eslint-disable-next-line vue/no-unused-emit-declarations
  emits: ['stepDelete', 'addStepBefore', 'stepChange'],
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    ...mapGetters({
      actionClipboard: 'wgptAutomations/getActionClipboard',
      getLinkedSteps: 'wgptAutomationBuilder/getLinkedSteps',
    }),
    draggableStep: {
      get() {
        return [this.step];
      },
      set(steps) {
        if (steps.length === 0) {
          this.onStepMove();
        } else if (steps.length > 1) {
          this.onStepDropped(steps);
        }
      },
    },
    isBeingCut() {
      const clipboard = this.actionClipboard;
      if (!clipboard) return false;
      const { action, operation } = clipboard;
      if (operation !== 'cut') return false;
      return action.id === this.step.id;
    },
    stepRoute() {
      if (this.v$.step.$error) {
        return { query: { selected: this.step.id, dirty: true } };
      }

      return { query: { selected: this.step.id } };
    },
    linkedStepRoute() {
      if (this.step.action !== 'jump_to_step') return null;

      const targetStepId = this.step.attributes?.step_id;
      if (!targetStepId) return null;

      return { query: { selected: targetStepId } };
    },
    linkedSteps() {
      return this.getLinkedSteps(this.step.id);
    },
    isActive() {
      return this.$route.query.selected === this.step.id;
    },
    isLinked() {
      const selected = this.$route.query.selected;
      return this.linkedSteps.some(step => step.id === selected);
    },
  },
  methods: {
    onStepDropped(steps) {
      const droppedStepsBefore = [];
      const currentStepId = this.step.id;

      steps.forEach(step => {
        if (step.id === currentStepId) return;

        droppedStepsBefore.push(step);
      });

      const selectedStepId = this.$route.query.selected;
      let newSelectedId = null;

      droppedStepsBefore.forEach(step => {
        const newId = getUuid();
        if (step.id === selectedStepId) newSelectedId = newId;
        this.$emit('addStepBefore', { ...step, id: newId });
      });

      if (newSelectedId) {
        this.$router
          .replace({ query: { selected: newSelectedId } })
          .catch(() => {});
      }
    },
    onStepMove() {
      setStepMoveInProgress(true);
      this.deleteStep();
      setStepMoveInProgress(false);
    },
    onCardMove(evt) {
      // Prevent dropping the branch into one of its own arms by checking if
      // the target container is inside this component's root element.
      const conditionEl = this.$el;
      const targetEl = evt.to;
      if (conditionEl && conditionEl.contains(targetEl)) return false;
      return true;
    },
    deleteStep() {
      this.$emit('stepDelete', this.step.id);
    },
    cutStep() {
      this.$store.dispatch('wgptAutomations/cutAction', this.step);
    },
    copyStep() {
      this.$store.dispatch('wgptAutomations/copyAction', this.step);
    },
    jumpToStep() {
      if (this.linkedStepRoute) {
        this.$router.push(this.linkedStepRoute);
      }
    },
  },
};
</script>

<template>
  <div class="condition mx-auto w-max">
    <div v-if="first" class="connector-top">
      <AddStepButton
        class="my-4"
        :class="{ invisible: disabled }"
        @add-step="e => $emit('addStepBefore', e)"
        @delete-step="e => $emit('stepDelete', e)"
      />
    </div>

    <Dropdown :disabled="disabled" class="mx-auto w-max">
      <Draggable
        v-model="draggableStep"
        item-key="id"
        group="steps"
        :move="onCardMove"
        class="draggable condition-draggable"
        :class="{
          'step-error': v$.step.$error,
          'step-active [&_.linked-button]:visible': isActive,
          'step-cut-active': isBeingCut,
          'step-linked': isLinked,
        }"
      >
        <template #item="{ element: stepEl }">
          <router-link :to="stepRoute" replace class="block">
            <div :id="stepEl.id" class="condition-content">
              <div
                class="py-2 px-3 border-b border-n-strong font-semibold text-sm"
              >
                <span class="text-base font-medium text-n-slate-12">
                  {{
                    stepEl.name ||
                    $t('WGPT_AUTOMATIONS.EDITOR.BRANCH.FALLBACK_CONDITION_NAME')
                  }}
                </span>
              </div>
              <div class="py-2 px-3">
                <p class="text-xs text-n-slate-11 m-0">
                  {{ $t('WGPT_AUTOMATIONS.REGISTRY.ACTIONS.BRANCH.NAME') }}
                </p>
              </div>
            </div>
          </router-link>
        </template>
      </Draggable>

      <template #popper>
        <div class="flex gap-1">
          <div class="bg-n-solid-2 rounded-md shadow-sm border border-n-weak">
            <ButtonNext
              color="slate"
              variant="ghost"
              size="xs"
              icon="i-lucide-copy"
              @click="copyStep"
            />
            <ButtonNext
              color="slate"
              variant="ghost"
              size="xs"
              icon="i-lucide-scissors"
              @click="cutStep"
            />
            <ButtonNext
              color="ruby"
              variant="ghost"
              size="xs"
              icon="i-lucide-trash"
              @click="deleteStep"
            />
          </div>
        </div>
      </template>
    </Dropdown>

    <div class="connector-bottom" />

    <div class="branches">
      <slot name="branch" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.condition-draggable {
  @apply relative bg-slate-100 dark:bg-slate-800/50 min-w-[200px] min-h-[71px] mx-2 rounded-xl;
}

.condition-content {
  @apply rounded-xl shadow-sm border border-n-strong bg-n-solid-2 dark:bg-n-solid-3;
  @apply w-full relative cursor-pointer;
}

.step-active:not(.error) {
  .condition-content {
    @apply outline-none;
    outline-color: var(--branchThemeColor);
    outline-offset: 1px;
  }
}

.step-cut-active {
  .condition-content {
    @apply opacity-50;
  }
}

.step-error {
  .condition-content {
    @apply outline-none outline-n-ruby-8;
    outline-offset: 1px;
  }
}

.step-linked:not(.step-error, .step-active) {
  .condition-content {
    @apply outline-none;
    outline-color: var(--linkedThemeColor);
    outline-offset: 1px;
    outline-style: dashed;
  }
}

[dir='ltr'] {
  .connector-top {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-r-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(50%);
  }

  .connector-bottom {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none h-8 border-r-[1px] border-dashed border-slate-400 dark:border-slate-600;
  }

  .branches {
    @apply flex justify-center;
    ::v-deep {
      > :first-child {
        > .connector-bridge {
          @apply hidden;
        }
        > .connector-top {
          @apply ms-auto items-start rounded-tl-2xl border-l-[1px] border-t-[1px];
          --transform: translateX(-50%);
        }
        > .connector-bottom {
          @apply ms-auto items-start border-l-[1px];
          --transform: translateX(-50%);
        }
      }
      > :not(:first-child, :last-child) {
        > .connector-top {
          @apply border-r-[1px];
        }
      }
      > :last-child {
        > .connector-bridge {
          @apply hidden;
        }
        > .connector-top {
          @apply me-auto items-end rounded-tr-2xl border-r-[1px] border-t-[1px];
          --transform: translateX(50%);
        }
        > .connector-bottom {
          @apply me-auto items-end border-r-[1px];
          --transform: translateX(50%);
        }
      }
    }
  }
}

[dir='rtl'] {
  .connector-top {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-l-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(-50%);
  }

  .connector-bottom {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none h-8 border-l-[1px] border-dashed border-slate-400 dark:border-slate-600;
  }
  .branches {
    @apply flex justify-center;
    ::v-deep {
      > :first-child {
        > .connector-bridge {
          @apply hidden;
        }
        > .connector-top {
          @apply ms-auto items-start rounded-tr-2xl border-s-[1px] border-t-[1px];
          --transform: translateX(50%);
        }
        > .connector-bottom {
          @apply ms-auto items-start border-s-[1px];
          --transform: translateX(50%);
        }
      }
      > :not(:first-child, :last-child) {
        > .connector-top {
          @apply border-l-[1px];
        }
      }
      > :last-child {
        > .connector-bridge {
          @apply hidden;
        }
        > .connector-top {
          @apply me-auto items-end rounded-tl-2xl border-e-[1px] border-t-[1px];
          --transform: translateX(-50%);
        }
        > .connector-bottom {
          @apply me-auto items-end border-e-[1px];
          --transform: translateX(-50%);
        }
      }
    }
  }
}

.sortable-ghost {
  @apply border-none h-[2px] w-full absolute;
  &::before {
    @apply block !bg-n-brand rounded-full absolute w-full;
    content: '';
    height: 4px;
  }
  &:first-child::before {
    @apply -mt-4;
  }
  &:last-child::before {
    @apply mt-3;
  }
  .condition-content {
    @apply opacity-0;
  }
}

.sortable-chosen .condition-content {
  outline-color: transparent !important;
}

.step-draggable .sortable-ghost:first-child:last-child::before {
  @apply hidden;
}

.branch-draggable .sortable-ghost:before {
  @apply -mt-7 mx-2;
}

.condition-draggable .sortable-ghost:last-child::before {
  @apply hidden;
}

.linked-button {
  color: var(--linkedThemeColor);
}
</style>
